// --- src/services/companyService.ts ---
import { supabase } from '@/lib/supabaseClient';
import { Company } from '../components/types';
import { DEFAULT_COLORS } from '../constants/colors';

export const companyService = {
  /**
   * Fetches company branding data for current user's company
   * Returns default values if company not found
   */
  async getCompanyBranding(): Promise<Company> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get user's company_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .eq('is_active', true)
        .single();

      if (profileError || !profile?.company_id) {
        throw new Error('Company not found');
      }

      // Get company branding data
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('id, name, logo_url, ui_settings')
        .eq('id', profile.company_id)
        .single();

      if (companyError || !company) {
        throw new Error('Company data not found');
      }

      // Merge UI settings with defaults
      const uiSettings = {
        ...DEFAULT_COLORS,
        ...(company.ui_settings as Company['ui_settings']),
      };

      return {
        id: company.id,
        name: company.name,
        logo_url: company.logo_url,
        ui_settings: uiSettings,
      };
    } catch (error) {
      console.error('[companyService] Error fetching branding:', error);
      // Return default branding (Solyom Barkács)
      return {
        id: '',
        name: 'Solyom barkácsbolt',
        logo_url: null,
        ui_settings: DEFAULT_COLORS,
      };
    }
  },

  /**
   * Fetches company branding by hostname (pre-login, public access)
   * Security: Only selects name, logo_url, and ui_settings (no sensitive data)
   * @param hostname - The hostname to search for (e.g., "demo.okoskeszlet.hu")
   * @returns Company branding data or default values
   */
  async getBrandingByHostname(hostname: string): Promise<Company> {
    try {
      // Extract subdomain from hostname
      // Examples: demo.okoskeszlet.hu -> "demo"
      //           okoskeszlet.hu -> "okoskeszlet"
      const parts = hostname.split('.');
      let searchTerm = '';

      if (parts.length >= 3) {
        // Has subdomain: demo.okoskeszlet.hu
        searchTerm = parts[0].toLowerCase(); // "demo"
      } else if (parts.length === 2) {
        // No subdomain: okoskeszlet.hu
        searchTerm = parts[0].toLowerCase(); // "okoskeszlet"
      }

      if (!searchTerm) {
        // Fallback to default
        return {
          id: '',
          name: 'Solyom barkácsbolt',
          logo_url: null,
          ui_settings: DEFAULT_COLORS,
        };
      }

      // SECURITY: Only select public branding fields (name, logo_url, ui_settings)
      // No sensitive company data is exposed
      const { data: companies, error } = await supabase
        .from('companies')
        .select('name, logo_url, ui_settings') // Only public fields
        .ilike('name', `%${searchTerm}%`)
        .limit(1);

      if (error || !companies || companies.length === 0) {
        // Fallback to default
        return {
          id: '',
          name: 'Solyom barkácsbolt',
          logo_url: null,
          ui_settings: DEFAULT_COLORS,
        };
      }

      const company = companies[0];

      // Merge UI settings with defaults
      const uiSettings = {
        ...DEFAULT_COLORS,
        ...(company.ui_settings as Company['ui_settings']),
      };

      return {
        id: '', // Not needed for pre-login branding
        name: company.name,
        logo_url: company.logo_url,
        ui_settings: uiSettings,
      };
    } catch (error) {
      console.error('[companyService] Error fetching branding by hostname:', error);
      // Return default branding (Solyom Barkács)
      return {
        id: '',
        name: 'Solyom barkácsbolt',
        logo_url: null,
        ui_settings: DEFAULT_COLORS,
      };
    }
  },

  /**
   * Fetches company branding by domain/hostname (pre-login)
   * Used to show company branding on login screen before authentication
   * @deprecated Use getBrandingByHostname instead
   */
  async getCompanyBrandingByDomain(): Promise<Company> {
    return this.getBrandingByHostname(window.location.hostname);
  },
};
